/**
 * Builder for per-module role permissions.
 */
import React, { useEffect, useState } from 'react';
import { Shield, AlertCircle, StickyNote, Info } from 'lucide-react';
import { DynamicIcon } from '@/components/forms/IconPicker';
import { Checkbox } from '@/components/forms/Checkbox';
import { ModuleAccessCard } from './ModuleAccessCard';
import { getAllInterfaceElementsForBuilder } from '@/services/interface-elements.service';
import {
  buildModuleGroups,
  canHavePostItsConfigAccess,
  countConfiguredActions,
  resolveModuleBinding,
  type ModuleGroup,
} from '@/components/roles/role-form-utils';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/utils/errors-utils';
import type { RoleBindingRules, RoleMode } from '@/types/role.types';
import type { InterfaceElement } from '@/types/interface-elements.types';

/** Post-it planning access flags stored alongside module permissions. */
interface PostItsAccess {
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
}

/** Props for {@link RolePermissionsBuilder}. */
interface RolePermissionsBuilderProps {
  value: Record<string, RoleBindingRules>;
  onChange: (bindings: Record<string, RoleBindingRules>) => void;
  onElementsLoaded?: (elements: Record<string, InterfaceElement[]>) => void;
  mode?: RoleMode | null;
  hierarchyLevel?: number;
  roleGroup?: string;
  disabled?: boolean;
  postItsAccess?: PostItsAccess;
}

/** Builder for per-module role permissions. */
export function RolePermissionsBuilder({
  value,
  onChange,
  onElementsLoaded,
  mode,
  hierarchyLevel,
  roleGroup,
  disabled,
  postItsAccess,
}: Readonly<RolePermissionsBuilderProps>): React.ReactElement | null {
  const [modules, setModules] = useState<ModuleGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInterfaceName, setSelectedInterfaceName] = useState<string | null>(null);

  const onElementsLoadedRef = React.useRef(onElementsLoaded);
  onElementsLoadedRef.current = onElementsLoaded;

  useEffect(() => {
    const loadElements = async () => {
      try {
        setLoading(true);
        const groupedElements = await getAllInterfaceElementsForBuilder();

        const moduleGroups: ModuleGroup[] = buildModuleGroups(groupedElements, mode);

        setModules(moduleGroups);
        setSelectedInterfaceName((prev) => {
          if (prev && moduleGroups.some((m) => m.interfaceName === prev)) return prev;
          return moduleGroups[0]?.interfaceName ?? null;
        });
        onElementsLoadedRef.current?.(groupedElements);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    void loadElements();
  }, [mode]);

  useEffect(() => {
    if (modules.length === 0) {
      setSelectedInterfaceName(null);
      return;
    }

    const stillExists = modules.some((m) => m.interfaceName === selectedInterfaceName);
    if (!selectedInterfaceName || !stillExists) {
      setSelectedInterfaceName(modules[0].interfaceName);
    }
  }, [modules, selectedInterfaceName]);

  const getModuleBinding = (interfaceName: string): RoleBindingRules =>
    resolveModuleBinding(value, modules, interfaceName);

  const updateModuleRules = (interfaceName: string, rules: RoleBindingRules['rules']) => {
    if (disabled) return;

    const newBindings = { ...value };
    newBindings[interfaceName] = { ...getModuleBinding(interfaceName), rules };
    onChange(newBindings);
  };

  const updateModuleTabs = (interfaceName: string, tabs: string[] | null | undefined) => {
    if (disabled) return;

    const newBindings = { ...value };
    newBindings[interfaceName] = { ...getModuleBinding(interfaceName), tabs };
    onChange(newBindings);
  };

  const actionsCountFor = (interfaceName: string): number =>
    countConfiguredActions(getModuleBinding(interfaceName));

  const postItsBlock = postItsAccess && (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-yellow-100 text-yellow-700">
          <StickyNote className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Configuration des post-its</h3>
          <p className="text-xs text-gray-600">
            Permet de planifier et assigner des post-its aux utilisateurs de rôles inférieurs dans
            la hiérarchie.
          </p>
        </div>
      </div>

      {canHavePostItsConfigAccess(roleGroup, hierarchyLevel) ? (
        <div
          className={cn(
            'rounded-lg border p-3 transition-all',
            postItsAccess.visible ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200 bg-gray-50',
          )}
        >
          <div className="flex items-center gap-3">
            <span className="flex-1 text-sm font-medium text-gray-800">
              Accès à la planification des post-its
            </span>
            <Checkbox
              checked={postItsAccess.visible}
              onCheckedChange={(v) => postItsAccess.onVisibleChange(!!v)}
              disabled={disabled}
            />
          </div>
          {postItsAccess.visible && (
            <p className="text-xs text-blue-700 mt-2">
              Les utilisateurs avec ce rôle pourront configurer des post-its pour les rôles sous eux
              dans la hiérarchie.
            </p>
          )}
        </div>
      ) : (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50/60">
          <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            Ce rôle ne peut pas accéder à la configuration des post-its car il n'a aucun utilisateur
            sous lui dans la hiérarchie des bénévoles.
          </p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Chargement des modules...</div>
        </div>
        {postItsBlock}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
        {postItsBlock}
      </div>
    );
  }

  if (modules.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-12 text-gray-500">Aucun module disponible pour ce mode.</div>
        {postItsBlock}
      </div>
    );
  }

  const selectedModule = modules.find((m) => m.interfaceName === selectedInterfaceName);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
        <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">Configuration des permissions par module</p>
          <p>
            Sélectionnez un module à gauche, puis configurez ses règles et actions à droite. Chaque
            règle peut filtrer par type d'entité, définir un périmètre de lecture et autoriser des
            actions spécifiques.
          </p>
        </div>
      </div>

      <div className="flex h-[900px] max-h-[calc(900vh-12rem)] rounded-lg border border-gray-200 overflow-hidden bg-white">
        <nav
          className="flex w-64 shrink-0 flex-col min-h-0 border-r border-gray-200 bg-gray-50"
          aria-label="Modules de permissions"
        >
          <div className="shrink-0 px-4 pt-3 pb-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Accès et permissions
            </p>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 scrollbar-thin">
            <ul className="space-y-0.5">
              {modules.map((module) => {
                const binding = getModuleBinding(module.interfaceName);
                const isSelected = module.interfaceName === selectedInterfaceName;
                const rulesCount = binding.rules.length;
                const actionsCount = actionsCountFor(module.interfaceName);

                return (
                  <li key={module.interfaceName}>
                    <button
                      type="button"
                      onClick={() => setSelectedInterfaceName(module.interfaceName)}
                      disabled={disabled}
                      aria-current={isSelected ? 'true' : undefined}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors',
                        isSelected
                          ? 'bg-white shadow-sm ring-1 ring-blue-200 text-blue-900'
                          : 'text-gray-700 hover:bg-gray-100',
                        disabled && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      {module.icon ? (
                        <DynamicIcon
                          name={module.icon}
                          className={cn(
                            'w-4 h-4 shrink-0',
                            isSelected ? 'text-blue-600' : 'text-gray-600',
                          )}
                        />
                      ) : (
                        <span className="w-4 h-4 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{module.moduleLabel}</div>
                        <div className="text-xs text-gray-500">
                          {rulesCount} règle{rulesCount > 1 ? 's' : ''}
                          {actionsCount > 0 &&
                            ` · ${actionsCount} action${actionsCount > 1 ? 's' : ''}`}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 bg-gray-50/60 scrollbar-thin">
          {selectedModule ? (
            <ModuleAccessCard
              key={selectedModule.interfaceName}
              moduleLabel={selectedModule.moduleLabel}
              moduleIcon={selectedModule.icon}
              interfaceName={selectedModule.interfaceName}
              elements={selectedModule.elements}
              rules={getModuleBinding(selectedModule.interfaceName).rules}
              tabs={getModuleBinding(selectedModule.interfaceName).tabs}
              onRulesChange={(rules) => updateModuleRules(selectedModule.interfaceName, rules)}
              onTabsChange={(tabs) => updateModuleTabs(selectedModule.interfaceName, tabs)}
              disabled={disabled}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              Sélectionnez un module pour configurer ses permissions
            </div>
          )}
        </div>
      </div>

      {postItsBlock}
    </div>
  );
}
